import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Share2, BookmarkCheck } from "lucide-react";
import { VerseSkeleton } from "@/components/Skeletons";

/**
 * interface VerseOfTheDayProps { 
 *   verse?: string; 
 *   reference?: string; 
 *   translation?: string; 
 *   isLoading?: boolean;
 *   onShare?: () => void;
 * }
 */
export default function VerseOfTheDay({ 
  verse = "Be still, and know that I am God: I will be exalted among the heathen, I will be exalted in the earth.", 
  reference = "Psalm 46:10", 
  translation = "KJV", 
  isLoading = false, 
  onShare 
}) {
  return (
    <section data-testid="verse-section" className="py-20 sm:py-28 bg-bone relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gold/5 rounded-full blur-[100px] pointer-events-none" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
        <div className="flex items-center justify-center gap-2 mb-8">
          <BookmarkCheck className="h-5 w-5 text-gold" />
          <span className="text-navy text-sm font-heading uppercase tracking-widest font-bold">
            Daily Discipleship
          </span>
        </div>

        {isLoading ? (
          <div className="max-w-2xl mx-auto">
            <VerseSkeleton />
          </div>
        ) : (
          <Card className="bg-white/80 backdrop-blur-sm border-t-4 border-t-gold border-x-bone-dark border-b-bone-dark rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-500 max-w-3xl mx-auto">
            <CardContent className="p-8 sm:p-14">
              <BookOpen className="h-8 w-8 text-gold/40 mx-auto mb-6" />
              
              <p className="text-navy text-2xl sm:text-3xl lg:text-4xl font-heading italic leading-relaxed mb-8">
                "{verse?.trim()}"
              </p>
              
              <p className="text-gold font-bold tracking-widest mb-10 uppercase text-sm sm:text-base font-sans">
                — {reference} {translation && <span className="opacity-70 text-xs ml-1">{translation}</span>}
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link 
                  to="/media?tab=bible" 
                  className="w-full sm:w-auto"
                  onClick={() => {
                    if (reference) {
                      try {
                        const bookMatch = reference.match(/^(\d?\s*[A-Za-z]+)/);
                        const chapMatch = reference.match(/(\d+):/);
                        if (bookMatch && chapMatch) {
                          const book = bookMatch[1].trim();
                          const chap = chapMatch[1];
                          localStorage.setItem("wagc_bible_last_position", JSON.stringify({ bookId: book, chapter: chap }));
                        }
                      } catch(e) {}
                    }
                  }}
                >
                  <Button
                    className="bg-navy text-gold hover:bg-navy/90 rounded-full text-xs font-bold h-12 px-8 shadow-md hover:-translate-y-0.5 transition-all duration-300 uppercase tracking-wider w-full"
                    data-testid="read-chapter-btn"
                  >
                    <BookOpen className="h-4 w-4 mr-2" /> Read Full Chapter
                  </Button>
                </Link>
                
                {onShare && (
                  <Button
                    variant="outline"
                    onClick={onShare}
                    className="border-navy/20 text-navy hover:bg-navy/5 rounded-full text-xs font-bold h-12 px-8 shadow-sm hover:-translate-y-0.5 transition-all duration-300 uppercase tracking-wider w-full sm:w-auto"
                    data-testid="share-verse-btn"
                  >
                    <Share2 className="h-4 w-4 mr-2 text-gold" /> Share
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
}